import * as IpAddress from 'ip-address'
import { ascend, prop, sortWith } from 'ramda'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { VmStorageOptions } from 'account/components/userManagement/tenants/VmStorageTypePicklist'
import { IPVersions } from 'app/constants'
import { customValidator } from 'core/utils/fieldValidators'
import { memoize } from 'utils/misc'
import type { Subnet } from '../networks/model'

export const CREATE_VM_MODAL_TITLE = 'Deploy New VM'
export const CREATE_VM_AVAILABILITY_ZONE_ERROR = 'The requested availability zone is not available'

// PCD-7835 QGA-FR-007: the ticket's command that starts the QEMU guest agent once installed
// (systemd, then the older `qemu-ga` name, then Alpine's OpenRC; never fails the boot).
export const QEMU_ENABLE_RUNCMD =
  '- [ sh, -c, "systemctl enable --now qemu-guest-agent 2>/dev/null || systemctl enable --now qemu-ga 2>/dev/null || { rc-update add qemu-guest-agent default && rc-service qemu-guest-agent start; } 2>/dev/null || true" ]'

// The example Cloud-init shown in the editor, with a comment above each value explaining it
// (PCD-8532). With QEMU guest agent auto-install on (PCD-7835), the install lines are added:
// the package above runcmd, the start command inside runcmd.
export const buildDefaultCloudInitYaml = (withQemuGuestAgent: boolean) =>
  [
    '#cloud-config',
    '# Cloud-init customizes this VM on the first boot only. It works',
    '# only on images that ship cloud-init. Most vendor cloud images',
    '# do. CirrOS does not, so nothing below will apply on CirrOS.',
    '# Full key reference: https://docs.cloud-init.io',
    '',
    "# Sets the password for the image's built-in user, not for root.",
    '# The username comes from the image: "ubuntu" on Ubuntu images,',
    '# "rocky" on Rocky, "cloud-user" on RHEL. Replace the value.',
    'password: CHANGE-ME-BEFORE-DEPLOY',
    '',
    '# Controls whether the password must be changed at first login.',
    '# false lets you log in with the password above straight away.',
    'chpasswd: { expire: false }',
    '',
    '# Allows SSH login using the password. Prefer false and attach an',
    '# SSH key in the field above, which is the more secure option.',
    'ssh_pwauth: true',
    '',
    '# Lets cloud-init rewrite /etc/hosts on every boot so the entry',
    '# always matches the VM hostname. Any manual edit to /etc/hosts',
    '# is overwritten. Set false to manage that file yourself.',
    'manage_etc_hosts: true',
    '',
    ...(withQemuGuestAgent
      ? [
          '# Refreshes the package list, then installs the QEMU guest agent',
          '# (added by "Auto-install QEMU Guest Agent").',
          'package_update: true',
          'packages:',
          '- qemu-guest-agent',
          '',
        ]
      : []),
    '# Commands run as root once, at the end of the first boot. Each',
    '# item is a string run by sh, or a list run directly. Replace the',
    '# example below with the setup steps you need.',
    'runcmd:',
    `- [ 'sh', '-c', 'echo "Hello World" > /var/tmp/helloworld.txt']`,
    ...(withQemuGuestAgent
      ? ['# Starts the QEMU guest agent once it is installed.', QEMU_ENABLE_RUNCMD]
      : []),
    '',
  ].join('\n')

export const placeholderCloudInitYaml = buildDefaultCloudInitYaml(false)

export type CreateVmSourceType =
  | 'image'
  | 'installFromIso'
  | 'newVolume'
  | 'existingVolume'
  | 'snapshot'
  | 'volumeSnapshot'

// PCD-5420 FR-014: One way the VM connects to a network. Each row becomes one NIC
// (network card) on the VM, with its own subnet and port. A managed network (one
// that has subnets) can have several rows, so the VM gets several NICs on it. Each
// row is identified by its own rowId, not by which subnet it uses, so letting the
// same subnet appear on two rows later (for VM high-availability) is just a
// validation tweak, not a structural change.
export interface NetworkAttachmentRow {
  rowId: string
  subnet: Subnet | '' // '' means "let the system pick" (Automatic)
  port: string // '' = Automatic | 'fixedIP' = type your own IP | otherwise an explicit port id
  fixedIP: string
  // Just tracks where this row came from in the UI: it was created by the parent
  // network's Ports dropdown, so it shows as a chip rather than its own sub-row.
  // Not set for rows added via "+ Add subnet". Has no effect on what we submit.
  viaPortChips?: boolean
  // Just tracks where this row came from in the UI: it was created by a sub-row's
  // Ports dropdown, so it shows as a chip inside that sub-row (whose rowId is stored
  // here) instead of as its own sub-row. Has no effect on what we submit.
  chipOf?: string
}

export type NetworkAttachments = Record<string, NetworkAttachmentRow[]>

// PCD-5420: a row's `port` field holds one of three things, all packed into a single
// string: '' (Automatic), 'fixedIP' (user types their own IP), or a real port id.
// The subnet field uses '' to mean Automatic too. These constants and helpers below
// are the one place that knows this encoding, so the rest of the code can ask clear
// questions ("is this an explicit port?") instead of comparing magic strings. Adding
// a new port mode later only means editing this block.
export const PORT_AUTOMATIC = ''
export const PORT_PRIVATE_IP = 'fixedIP'
export const SUBNET_AUTOMATIC = ''

// True when `port` is a real Neutron port id (not Automatic and not Private IP).
export const isExplicitPort = (port: string): boolean =>
  port !== PORT_AUTOMATIC && port !== PORT_PRIVATE_IP

// True only for Automatic. Automatic lets Nova (the compute service) pick any free
// address; Private IP or a specific port locks onto one address, so those can't be
// reused when creating a batch of VMs.
export const isAutomaticPort = (port: string): boolean => port === PORT_AUTOMATIC

// True for "Private IP" mode, where the user types a fixed IP and Nova assigns it on the subnet.
export const isPrivateIpPort = (port: string): boolean => port === PORT_PRIVATE_IP

// True when this row needs a brand-new IP/port from the network's pool. Both Automatic
// and Private IP do; an explicit port brings its own address, so it needs nothing new.
export const isAutoAllocatingPort = (port: string): boolean => !isExplicitPort(port)

// True when the row has a real subnet picked (not Automatic). As a TypeScript type guard
// it also lets callers read `row.subnet.id` directly without a cast.
export const hasExplicitSubnet = (
  row: NetworkAttachmentRow,
): row is NetworkAttachmentRow & { subnet: Subnet } => row.subnet !== SUBNET_AUTOMATIC

export interface CreateVmFormParams extends Record<string, unknown> {
  name: string
  hostname: string
  description: string
  sourceType: CreateVmSourceType | string
  image: any[]
  installerIso: any[]
  driverIso: any[]
  enableDriverIso: boolean
  attachVolumes: any[]
  volume: any[]
  volumeSize: string
  volumeType: string
  volumeSnapshot: any[]
  flavor: any[]
  deleteOnTermination: boolean
  deleteOnTerminationAdditionalVolumes: boolean
  availabilityZone: string | null
  enableHotplugging: boolean
  gpuCompatible: boolean
  networks: any[]
  // PCD-5420 FR-014: the NIC rows for each selected managed network (the Create VM flow)
  networkAttachments: NetworkAttachments
  // Older maps that only allow one attachment per network (still used by CloneVirtualMachineModal)
  selectedPorts: Record<string, string>
  fixedIPs: Record<string, string>
  selectedSubnets: Record<string, Subnet>
  count: number
  sshKey: string | null
  serverGroup: any | null
  useCloudInit: boolean
  useSecurityGroups: boolean
  cloudInitYaml: string
  // PCD-7835 QGA-FR-006: whether to auto-install the QEMU guest agent via cloud-init on first boot
  autoInstallQemuGuestAgent: boolean
  securityGroups: any[]
  metadata: any[]
  hotplugMemory: number | null
  hotplugMemoryMax: number | null
  hotplugVcpu: number | null
  hotplugVcpuMax: number | null
  priority: string
  stretchedCluster: string
}

interface CreateVmSourceTypeOption {
  label: string
  value: CreateVmSourceType
}

interface FilterCreateVmNetworksArgs {
  currentTenant?: any
  isSelfServiceUser: boolean
  networks: any[]
}

interface FilterAvailableCreateVmVolumesArgs {
  availableVolumes: any[]
  selectedBootVolumes: any[]
  sourceType: string
}

interface GetFilteredCreateVmImagesArgs {
  imageSort: string
  imageType: string
  images: any[]
  sourceType: string
  tenantVmStorageType?: VmStorageOptions
}

interface GetFilteredCreateVmFlavorsArgs {
  enableHotplugging: boolean
  flavorSort: string
  flavors: any[]
  gpuCompatible: boolean
  isVolumeSourceType: boolean
}

const allVmSourceTypeOptions: CreateVmSourceTypeOption[] = [
  { label: 'Image', value: 'image' },
  { label: 'Install from ISO', value: 'installFromIso' },
  { label: 'New Volume', value: 'newVolume' },
  { label: 'Existing Volume', value: 'existingVolume' },
  { label: 'VM Snapshot', value: 'snapshot' },
  { label: 'Volume Snapshot', value: 'volumeSnapshot' },
]

const isHotpluggableFlavor = (flavor) => flavor?.vcpus === 0 && flavor?.ram === 0

// NetworkingStep FR-009: Checks that the IP the user typed falls inside one of the
// network's subnet address ranges (CIDR). Empty input passes (the field is optional).
export const getPrivateIpValidator = (network) => {
  return customValidator((value) => {
    if (!value) {
      return true
    }

    try {
      const subnets = network?.subnetDetails
      const pass = subnets?.some((subnet) => {
        const testCidr =
          subnet?.ip_version === IPVersions.IPv4
            ? new IpAddress.Address4(subnet?.cidr)
            : new IpAddress.Address6(subnet?.cidr)

        return subnet?.ip_version === IPVersions.IPv4
          ? new IpAddress.Address4(value).isInSubnet(testCidr)
          : new IpAddress.Address6(value).isInSubnet(testCidr)
      })

      return pass
    } catch (err) {
      return false
    }
  }, 'IP Address must be within one of the subnet CIDRs')
}

// CustomizeStep FR-001: Number of VMs to create must be between 1 and 20.
export const countValidator = customValidator((value) => {
  return value >= 1 && value <= 20
}, 'Count must be between 1 and 20')

// FlavorStep/HotplugVmDialog FR-002: a max is only considered "set" when it's a finite number
// greater than 0 — treating 0/NaN as falsy-but-unset (rather than a real, if degenerate, cap) is
// how a max of 0 used to silently disable enforcement instead of blocking hotplug entirely.
// Accepts string metadata values (e.g. HOTPLUG_MEMORY_MAX) or numbers.
export const isHotplugMaxSet = (max?: string | number): boolean => {
  const numericMax = Number(max)
  return Number.isFinite(numericMax) && numericMax > 0
}

// FlavorStep FR-002: Hotplug RAM amount must be greater than 0 and at most the hotplug
// RAM maximum (the requested value acts like a k8s "request", the maximum like a "limit").
// The RAM fields use SizeInputWithUnit, so the on-screen value is in the user-selected unit
// (MiB or GiB) while params hold the MiB-converted number — validate the converted MiB
// params rather than the raw field value, which keeps the check unit-independent.
export const hotplugMemoryMiBValidator = memoize((memoryMiB: number, maxMiB: number) =>
  customValidator(
    () => (isHotplugMaxSet(maxMiB) ? memoryMiB > 0 && memoryMiB <= maxMiB : memoryMiB > 0),
    'Value must be greater than 0 and less than or equal to the hotplug RAM maximum',
  ),
)

// FlavorStep FR-002: Hotplug vCPU count must be greater than 0 and at most the hotplug
// vCPU maximum.
export const hotplugVcpuValidator = customValidator((value, formValues) => {
  const max = Number(formValues?.hotplugVcpuMax)
  return isHotplugMaxSet(max) ? value > 0 && value <= max : value > 0
}, 'Value must be greater than 0 and less than or equal to the hotplug vCPU maximum')

// FlavorStep FR-002: Hotplug vCPU maximum must be greater than 0 and at most 255
// (libvirt/QEMU vCPU limit).
export const hotplugVcpuMaxValidator = customValidator((value) => {
  return value >= 1 && value <= 255
}, 'Value must be between 1 and 255')

// FR-013: Checks the guest OS hostname. It must start and end with a letter or number,
// may contain hyphens and underscores in between, and be at most 253 characters.
export const hostnameValidator = customValidator((value) => {
  if (!value) return true // hostname is optional, so blank is fine
  // Start and end with a letter/number; hyphens and underscores allowed in the middle
  return /^[a-zA-Z0-9]([a-zA-Z0-9\-_]*[a-zA-Z0-9])?$/.test(value) && value.length <= 253
}, 'Hostname must start/end with alphanumeric, contain only alphanumeric/hyphens/underscores, and be ≤ 253 chars')

// Image and snapshot sources skip the "Attach Volumes" step, so every later step
// shifts down by one. This maps a logical step number to the one actually shown.
export const getCreateVmStepNumber = (sourceType: string, step: number) =>
  ['image', 'snapshot'].includes(sourceType) ? step - 1 : step

export const createDefaultCreateVmParams = (
  disabledBootFromVolume: boolean,
): CreateVmFormParams => ({
  name: '',
  hostname: '',
  description: '',
  sourceType: disabledBootFromVolume ? 'image' : 'newVolume',
  image: [],
  installerIso: [],
  driverIso: [],
  enableDriverIso: false,
  attachVolumes: [],
  volume: [],
  volumeSize: '20',
  volumeType: '',
  volumeSnapshot: [],
  flavor: [],
  deleteOnTermination: false,
  deleteOnTerminationAdditionalVolumes: false,
  availabilityZone: null,
  enableHotplugging: false,
  gpuCompatible: false,
  networks: [],
  networkAttachments: {},
  selectedPorts: {},
  fixedIPs: {},
  selectedSubnets: {},
  count: 1,
  sshKey: null,
  serverGroup: null,
  useCloudInit: false,
  useSecurityGroups: false,
  cloudInitYaml: placeholderCloudInitYaml,
  // PCD-7835 QGA-FR-006: default OFF, like Use Cloud-init. Turning it on turns cloud-init on.
  autoInstallQemuGuestAgent: false,
  securityGroups: [],
  metadata: [],
  hotplugMemory: null,
  hotplugMemoryMax: 65536,
  hotplugVcpu: null,
  hotplugVcpuMax: 64,
  priority: '',
  stretchedCluster: '',
})

export const getTenantVmStorageType = (
  tenants: any[] = [],
  currentTenant?: any,
): VmStorageOptions | undefined => {
  const tenant = tenants.find((item) => item.id === currentTenant)
  return tenant?.tags?.find((tag) => tag.startsWith('vm_storage_type='))?.split('=')[1]
}

export const getCreateVmSourceTypeOptions = (
  tenantVmStorageType?: VmStorageOptions,
): CreateVmSourceTypeOption[] => {
  const ephemeralOptions = allVmSourceTypeOptions.filter((option) =>
    ['image', 'snapshot'].includes(option.value),
  )

  const volumeOptions = allVmSourceTypeOptions.filter((option) =>
    ['installFromIso', 'newVolume', 'existingVolume', 'snapshot', 'volumeSnapshot'].includes(
      option.value,
    ),
  )

  switch (tenantVmStorageType) {
    case VmStorageOptions.ephemeral:
      return ephemeralOptions
    case VmStorageOptions.volume:
      return volumeOptions
    case VmStorageOptions.ephemeral_and_volume:
    default:
      return allVmSourceTypeOptions
  }
}

export const getAllowedVmSnapshotTypes = (tenantVmStorageType?: VmStorageOptions): string[] => {
  switch (tenantVmStorageType) {
    case VmStorageOptions.ephemeral:
      return ['VM Ephemeral Disk Snapshot']
    case VmStorageOptions.volume:
      return ['VM Volume Snapshot']
    case VmStorageOptions.ephemeral_and_volume:
    default:
      return ['VM Ephemeral Disk Snapshot', 'VM Volume Snapshot']
  }
}

// FR-001: Picks which networks the user is allowed to attach to. Keeps a network only
// if it's usable (managed, meaning it has subnets, or simple/Layer 2 with none) and the
// user has access: shared networks always pass; otherwise it must belong to the current
// tenant (project). Self-service users additionally can't use external networks.
export const filterCreateVmNetworks = ({
  networks,
  currentTenant,
  isSelfServiceUser,
}: FilterCreateVmNetworksArgs): any[] =>
  networks.filter((network) => {
    const isManagedNetwork = network.subnets?.length > 0
    const isSimpleNetwork = network.isSimpleNetwork

    if (!isManagedNetwork && !isSimpleNetwork) {
      return false
    }

    if (network.shared) {
      return true
    }

    if (network.tenant_id !== currentTenant) {
      return false
    }

    if (isSelfServiceUser) {
      return !network?.['router:external']
    }

    return true
  })

// FR-002: Counts how many managed networks (with subnets) and how many simple/Layer 2
// networks (without subnets) are available, so the mode-selection cards can show the totals.
export const getCreateVmNetworkCounts = (networks: any[] = []) => ({
  managedNetworksCount: networks.filter(
    (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
  ).length,
  simpleNetworksCount: networks.filter((network) => network.isSimpleNetwork).length,
})

// FR-003: Shows only volumes with status "available". When the VM boots from an existing
// volume, also hides any volume already chosen as the boot volume so it can't be picked twice.
export const filterAvailableCreateVmVolumes = ({
  availableVolumes,
  sourceType,
  selectedBootVolumes,
}: FilterAvailableCreateVmVolumesArgs): any[] =>
  availableVolumes.filter((volume) => {
    if (volume.status !== 'available') {
      return false
    }

    if (sourceType !== 'existingVolume') {
      return true
    }

    return !selectedBootVolumes.includes(volume)
  })

// FR-004: Picks the images to show, based on what the VM is being created from. Only
// "active" images count. For a snapshot source it narrows to the matching snapshot kind
// (ephemeral-disk vs volume) using the chosen type and the tenant's storage setting; for
// other sources it hides snapshots and ISO images. Finally sorts by name or size if asked.
export const getFilteredCreateVmImages = ({
  images,
  sourceType,
  imageType,
  tenantVmStorageType,
  imageSort,
}: GetFilteredCreateVmImagesArgs): any[] => {
  const filteredImages = images.filter((image) => {
    if (image.status !== 'active') {
      return false
    }

    const isEphemeralSnapshot = image.imageType === 'ephemeralDiskSnapshot'
    const isVolumeSnapshot = image.imageType === 'vmVolumeSnapshot'

    if (sourceType !== 'snapshot') {
      return !(isEphemeralSnapshot || isVolumeSnapshot || image.disk_format === 'iso')
    }

    if (
      imageType === 'VM Ephemeral Disk Snapshot' ||
      tenantVmStorageType === VmStorageOptions.ephemeral
    ) {
      return isEphemeralSnapshot
    }

    if (imageType === 'VM Volume Snapshot' || tenantVmStorageType === VmStorageOptions.volume) {
      return isVolumeSnapshot
    }

    return isEphemeralSnapshot || isVolumeSnapshot
  })

  const sorter = {
    name: sortWith([ascend(prop('name'))]),
    virtualSize: sortWith([ascend(prop('virtual_size'))]),
  }[imageSort]

  return imageSort ? sorter(filteredImages) : filteredImages
}

// FR-006: Of the managed networks, returns the one with the fewest free IPs/ports. That
// network is the bottleneck for how many VMs a batch can use, so we surface it as a warning.
export const getMinAvailableManagedNetwork = (networks: any[] = []) => {
  const managedNetworks = networks.filter((network) => !network.isSimpleNetwork)

  if (managedNetworks.length === 0) {
    return null
  }

  return managedNetworks.reduce((minNetwork, currentNetwork) =>
    currentNetwork?.availablePortAndIPs < minNetwork?.availablePortAndIPs
      ? currentNetwork
      : minNetwork,
  )
}

// PCD-5420 FR-019: Counts how many rows on a network each need a fresh IP/port from its
// pool. Only Automatic or Private IP rows do; an explicit-port row brings its own address
// and uses nothing. A network with no rows is treated as one such row (the old single-NIC
// behavior).
const countAutoAllocatingRows = (rows?: NetworkAttachmentRow[]): number =>
  rows?.length ? rows.filter((row) => isAutoAllocatingPort(row.port)).length : 1

// PCD-5420 FR-019: Finds the first managed network that doesn't have enough free IPs/ports
// for the request (needs VM count × its auto-allocating rows). Returns null if all are fine.
export const getInsufficientIpManagedNetwork = (
  networks: any[] = [],
  attachments: NetworkAttachments = {},
  count = 1,
): { network: any; required: number } | null => {
  for (const network of networks) {
    if (network?.isSimpleNetwork) {
      continue
    }
    const required = count * countAutoAllocatingRows(attachments[network.id])
    if (network?.availablePortAndIPs < required) {
      return { network, required }
    }
  }
  return null
}

// PCD-5420 FR-014: Makes a fresh NIC row with everything on Automatic (system picks the
// subnet and port) and a new unique id.
export const createAttachmentRow = (): NetworkAttachmentRow => ({
  rowId: uuidv4(),
  subnet: SUBNET_AUTOMATIC,
  port: PORT_AUTOMATIC,
  fixedIP: '',
})

// PCD-5420 FR-017: Keeps the NIC rows lined up with which networks are selected. A newly
// selected network starts with one default row; a network that keeps its existing rows
// keeps them; a deselected network drops out entirely.
export const syncNetworkAttachments = (
  selectedNetworks: any[],
  attachments: NetworkAttachments = {},
): NetworkAttachments =>
  Object.fromEntries(
    (selectedNetworks ?? []).map((network) => [
      network.id,
      attachments[network.id]?.length ? attachments[network.id] : [createAttachmentRow()],
    ]),
  )

// PCD-5420 FR-014: Adds another NIC row to a network (the "+ Add subnet" action). It
// leaves the existing rows alone, so an Automatic parent row stays Automatic. The new row
// defaults to the first subnet not already used by another row, so it lands on something
// usable. If the network has no subnets, the new row is just a plain default row.
export const addAttachmentRow = (
  attachments: NetworkAttachments,
  networkId: string,
  network?: any,
): NetworkAttachments => {
  const existing = attachments[networkId] ?? []
  const subnets: Subnet[] = network?.subnetDetails ?? []

  if (!subnets.length) {
    return { ...attachments, [networkId]: [...existing, createAttachmentRow()] }
  }

  const usedIds = new Set(existing.filter(hasExplicitSubnet).map((row) => row.subnet.id))
  const nextSubnet = subnets.find((s) => !usedIds.has(s.id)) ?? subnets[0]

  return {
    ...attachments,
    [networkId]: [...existing, { ...createAttachmentRow(), subnet: nextSubnet }],
  }
}

// PCD-5420 FR-017: Removes a NIC row. A network always keeps at least one row, so the
// last one can't be removed here (to drop it, deselect the whole network instead).
export const removeAttachmentRow = (
  attachments: NetworkAttachments,
  networkId: string,
  rowId: string,
): NetworkAttachments => {
  const rows = attachments[networkId] ?? []
  if (rows.length <= 1) {
    return attachments
  }
  // Removing a sub-row also removes any port chips that were attached to it
  const remaining = rows.filter((row) => row.rowId !== rowId && row.chipOf !== rowId)
  return { ...attachments, [networkId]: remaining.length ? remaining : [createAttachmentRow()] }
}

// PCD-5420 FR-015: Updates one NIC row's fields. Changing the subnet clears that row's
// port and fixed IP, because the list of selectable ports depends on the subnet, so a port
// chosen for a different subnet would no longer make sense. Choosing a port does NOT change
// the subnet.
export const updateAttachmentRow = (
  attachments: NetworkAttachments,
  networkId: string,
  rowId: string,
  changes: Partial<Omit<NetworkAttachmentRow, 'rowId'>>,
  network?: any,
): NetworkAttachments => ({
  ...attachments,
  [networkId]: (attachments[networkId] ?? []).map((row) => {
    if (row.rowId !== rowId) {
      return row
    }
    // Changing the subnet clears this row's port and fixed IP (the port list depends on the
    // subnet) — unless this same update also sets a port, in which case that combined edit wins.
    const subnetChanged = 'subnet' in changes && changes.subnet !== row.subnet
    const clearPort = subnetChanged && !('port' in changes)
    return clearPort
      ? { ...row, ...changes, port: PORT_AUTOMATIC, fixedIP: '' }
      : { ...row, ...changes }
  }),
})

// PCD-5420 FR-018: Lists the subnet IDs already picked by the other rows of a network
// (optionally skipping one row). This feeds the duplicate-subnet warning — using the same
// subnet on more than one row is allowed, we just flag it.
export const getUsedSubnetIds = (
  attachments: NetworkAttachments,
  networkId: string,
  excludeRowId?: string,
): string[] =>
  (attachments[networkId] ?? [])
    .filter((row) => row.rowId !== excludeRowId)
    .filter(hasExplicitSubnet)
    .map((row) => row.subnet.id)

// FR-007: True when the VM boots from a volume (persistent disk) rather than ephemeral
// storage — i.e. ISO install, new volume, existing volume, volume snapshot, or a VM
// snapshot that was itself volume-based.
export const isCreateVmVolumeSourceType = (params: CreateVmFormParams): boolean =>
  ['installFromIso', 'newVolume', 'existingVolume', 'volumeSnapshot'].includes(params.sourceType) ||
  (params.sourceType === 'snapshot' && params?.image?.[0]?.imageType === 'vmVolumeSnapshot')

// FR-005: Picks which flavors (VM size presets) to show.
//
// Hotplug flavors (vcpus===0 && ram===0) are placeholders with no usable CPU/RAM on
// their own — valid only once "Hot-plug Compatible" is on (user supplies
// hotplugMemory/hotplugVcpu separately). Hidden by default; shown only when
// enableHotplugging is true.
//
// GPU flavors carry a GPU passthrough/vGPU trait. They are only visible when
// gpuCompatible is explicitly on — they never appear in the regular (both-off) list.
//
// Toggle state → what's shown:
//   - Both off:  regular (non-hotplug, non-GPU) flavors, via the disk check below
//   - Hotplug on: hotplug flavors only (regardless of gpuCompatible)
//   - GPU on:     GPU flavors only (regardless of enableHotplugging)
//   - Both on:    flavors that are BOTH hotplug AND GPU (intersection, not union)
//
// Disk-size convention (applies only when neither toggle is on):
//   - Volume-backed VM: Cinder volume supplies the root disk, so only disk===0
//     flavors are valid (non-zero flavor disk would be unused space).
//   - Image-backed VM: the flavor's disk supplies the root disk, so only disk!==0
//     flavors are valid (disk===0 leaves no room for the image's root filesystem).
export const getFilteredCreateVmFlavors = ({
  flavors,
  flavorSort,
  enableHotplugging,
  gpuCompatible,
  isVolumeSourceType,
}: GetFilteredCreateVmFlavorsArgs): any[] => {
  const filteredFlavors = flavors.filter((flavor) => {
    const isHotplug = isHotpluggableFlavor(flavor)
    const isGpu = flavor?.gpuFlavor

    // A hotplug placeholder flavor only ever belongs in the list when hotplugging is on —
    // check this before the GPU branch so a flavor that's both hotplug and GPU-tagged can't
    // slip into the regular GPU list while the hotplug toggle is off.
    if (isHotplug && !enableHotplugging) return false

    // Hotplug toggle on but flavor isn't hotplug: drop.
    if (enableHotplugging && !isHotplug) return false

    // GPU toggle on but flavor isn't GPU: drop.
    if (gpuCompatible && !isGpu) return false

    // Hotplug placeholder flavors are disk-0 by design, so the disk-size convention
    // below does not apply to them.
    if (enableHotplugging) return true

    // GPU flavor but toggle is off: drop. Guard placed after hotplug early-return so
    // hotplug+GPU flavors still appear in the hotplug list when gpuCompatible is off.
    if (!gpuCompatible && isGpu) return false

    // Default and GPU lists: the disk-size convention applies — a volume-backed VM needs a
    // zero-disk flavor, an image-backed VM needs a flavor with room for the image's root
    // filesystem (disk !== 0).
    return isVolumeSourceType ? flavor?.disk === 0 : flavor?.disk !== 0
  })

  const sorter = {
    name: sortWith([ascend(prop('name'))]),
    vcpus: sortWith([ascend(prop('vcpus'))]),
    ram: sortWith([ascend(prop('ram'))]),
    disk: sortWith([ascend(prop('disk'))]),
  }[flavorSort]

  return flavorSort ? sorter(filteredFlavors) : filteredFlavors
}

// True when only one VM can be created: installing from ISO, booting from an existing
// volume, or attaching extra volumes all rule out making a batch.
export const shouldForceSingleVmCount = (params: CreateVmFormParams): boolean =>
  params?.sourceType === 'installFromIso' ||
  params?.sourceType === 'existingVolume' ||
  params.attachVolumes.length > 0

// FR-008: Decides whether creating more than one VM at once is allowed. Returns
// { disabled, message }: disabled is true (with a reason to show the user) when the
// source type, attached volumes, a pinned port/IP, or a Layer 2 network without the batch
// feature flag rules out batches. Otherwise returns disabled: false with an empty message.
export const getDisableMultipleCreateVmsState = (
  params: CreateVmFormParams,
  enableSimpleNetworkBatchCreation: boolean,
) => {
  if (params.sourceType === 'installFromIso') {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported when installing from ISO.',
    }
  }

  if (params.sourceType === 'existingVolume') {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported if VM is created from existing volume  ',
    }
  }

  if (params.attachVolumes.length > 0) {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported if additional volumes are attached to the VM',
    }
  }

  // FR-019: if any NIC row locks onto a specific address (an explicit port or a Private IP), only
  // one VM can be created — a single port can't be shared across a batch. Both managed and simple
  // (L2) networks keep their port choice on the attachment row, so this one check covers both.
  // When a network has no rows (the Clone modal), fall back to the older selectedPorts map.
  if (
    params?.networks?.some((network) => {
      const rows = params?.networkAttachments?.[network.id]
      if (rows?.length) {
        return rows.some((row) => !isAutomaticPort(row.port))
      }
      return !isAutomaticPort(params?.selectedPorts?.[network.id] ?? PORT_AUTOMATIC)
    })
  ) {
    return {
      disabled: true,
      message: 'You can create only 1 VM when a specific port is selected from a network.',
    }
  }

  if (
    !enableSimpleNetworkBatchCreation &&
    params?.networks?.some((network) => network.isSimpleNetwork)
  ) {
    return {
      disabled: true,
      message:
        'Batch VM creation with Layer 2 Networks is not supported. Please create VMs one at a time.',
    }
  }

  return {
    disabled: false,
    message: '',
  }
}

// FR-009: True when the VM's root disk is ephemeral (lives only as long as the VM, not on
// a persistent volume) — that's the case for image sources and ephemeral-disk snapshots.
export const isCreateVmUsingEphemeralRootDisk = (params: CreateVmFormParams): boolean =>
  params?.sourceType === 'image' ||
  (params?.sourceType === 'snapshot' && params?.image?.[0]?.imageType === 'ephemeralDiskSnapshot')

// FR-010: True when the VM is being built from a Windows image. It checks the OS type in
// the right place depending on the source (the image, an existing volume's image metadata,
// or the volume behind a volume snapshot). This decides whether we show the admin password.
export const isWindowsVmSource = (
  params: CreateVmFormParams,
  getVolumeSnapshotSourceVolume: (volumeSnapshot: any) => any,
): boolean => {
  if (['image', 'newVolume', 'snapshot'].includes(params.sourceType)) {
    return params?.image?.[0]?.os_type?.toLowerCase() === 'windows'
  }

  if (params.sourceType === 'existingVolume') {
    return params?.volume?.[0]?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
  }

  if (params.sourceType === 'volumeSnapshot') {
    const sourceVolume = getVolumeSnapshotSourceVolume(params?.volumeSnapshot?.[0])
    return sourceVolume?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
  }

  return false
}

// FR-014, FR-015: Keeps only the networks that the selected cluster can actually reach.
// A network's `provider:physical_network` must be one of the cluster's physical-network
// labels (worked out ahead of time in clusterHostGroupsSelector). Two cases skip filtering
// and return all networks: no cluster picked yet, or the cluster has no labels (its hosts
// aren't configured).
// (networks is typed any[] to match filterCreateVmNetworks; the enriched network has no shared model type.)
export const filterNetworksByClusterLabels = (
  networks: any[],
  clusterName: string | null,
  applicableLabels: string[],
): any[] => {
  if (!clusterName) return networks

  // Cluster has no labels (its hosts aren't configured yet), so don't filter anything out
  if (!applicableLabels?.length) return networks

  const labelSet = new Set(applicableLabels)

  return networks?.filter((network) => {
    const physicalNetwork = network?.['provider:physical_network']
    // FR-016: overlay networks (e.g. VXLAN) have no physical_network because they run over
    // tunnels and are reachable from any cluster, so we always keep them
    if (!physicalNetwork) return true
    return labelSet.has(physicalNetwork)
  })
}

// FR-017: When the user switches clusters (the availabilityZone), this clears any networks
// and subnets they'd already chosen, since those may not apply to the new cluster. It's a
// named hook on its own so the reset behavior can be tested in isolation.
export const useResetNetworksOnClusterChange = ({
  availabilityZone,
  networks,
  selectedSubnets,
  updateParams,
}: {
  availabilityZone: string | null | undefined
  networks: any[]
  selectedSubnets: Record<string, Subnet> | null | undefined
  updateParams: (params: Partial<CreateVmFormParams>) => void
}): void => {
  useEffect(() => {
    if (networks?.length > 0 || Object.keys(selectedSubnets || {}).length > 0) {
      updateParams({ networks: [], selectedSubnets: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityZone])
}
